import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { SignOut, ArrowLeft } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isGateOnlyUser } from "@/lib/gatePass";
import GateScanner from "@/components/gate/GateScanner";
import GateErrorBoundary from "@/components/gate/GateErrorBoundary";
import GateCapture from "@/components/gate/GateCapture";
import GateVehicleLog from "@/components/gate/GateVehicleLog";
import { ScanResult, PassRow, LogRow } from "@/components/gate/GateParts";

const today = () => new Date().toISOString().slice(0, 10);

const Section = ({ title, testId, empty, children }) => (
  <section className="border border-canvas-line bg-canvas-card" data-testid={testId}>
    <div className="px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">{title}</div>
    {empty ? <div className="px-3 py-3 text-xs text-ink-subtle border-t border-canvas-line">{empty}</div> : children}
  </section>
);

// Mobile gate page: scan passes (photo + km), log other vehicles, live registers.
export default function GatePage() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("pass");
  const [lookup, setLookup] = useState(null);
  const [result, setResult] = useState(null);
  const [out, setOut] = useState([]);
  const [passes, setPasses] = useState([]);
  const [log, setLog] = useState([]);
  const canScan = user?.role === "admin" || (user?.permissions || []).includes("gate.scan");

  const load = useCallback(async () => {
    const [a, b, c] = await Promise.all([
      api.get("/gate-passes", { params: { kind: "test_ride", status: "out" } }),
      api.get("/gate-passes", { params: { date: today() } }),
      api.get("/gate-passes/log", { params: { date: today() } }),
    ]);
    setOut(a.data);
    setPasses(b.data);
    setLog(c.data);
  }, []);

  useEffect(() => {
    load().catch(() => {});
    const t = setInterval(() => load().catch(() => {}), 30000);
    return () => clearInterval(t);
  }, [load]);

  const done = (r) => { setLookup(null); setResult(r); load().catch(() => {}); };

  return (
    <div className="min-h-screen bg-canvas max-w-xl mx-auto" data-testid="gate-page">
      <header className="flex items-center justify-between px-4 py-3 bg-ink text-white">
        <div className="flex items-center gap-2">
          {!isGateOnlyUser(user) && <Link to="/service" data-testid="gate-back" className="p-1"><ArrowLeft size={18} /></Link>}
          <div>
            <div className="text-sm font-bold tracking-wide">GATE</div>
            <div className="text-[10px] font-mono opacity-70">{user?.name}</div>
          </div>
        </div>
        <button type="button" onClick={logout} data-testid="gate-logout" className="p-2"><SignOut size={18} /></button>
      </header>
      <main className="p-4 space-y-5">
        {canScan && (
          <div className="grid grid-cols-2 border border-ink" data-testid="gate-tabs">
            {[["pass", "Scan Pass"], ["log", "Log Vehicle"]].map(([v, l]) => (
              <button key={v} type="button" onClick={() => { setTab(v); setResult(null); }} data-testid={`gate-tab-${v}`}
                className={`py-3 text-sm font-bold ${tab === v ? "bg-ink text-white" : "bg-canvas"}`}>{l}</button>
            ))}
          </div>
        )}
        <GateErrorBoundary>
          {canScan && tab === "pass" && (
            <div className={lookup ? "hidden" : ""}>
              <GateScanner onLookup={(d) => { setResult(null); setLookup(d); }} onResult={setResult} />
            </div>
          )}
          {canScan && tab === "pass" && lookup && <GateCapture lookup={lookup} onDone={done} onCancel={() => setLookup(null)} />}
          {canScan && tab === "log" && <GateVehicleLog onSaved={() => load().catch(() => {})} />}
          <ScanResult result={result} />
        </GateErrorBoundary>
        <Section title={`Test rides out now (${out.length}) · limit 10 min`} testId="gate-rides-out" empty={!out.length && "No vehicle out on test ride."}>
          {out.map((p) => <PassRow key={p.id} p={p} />)}
        </Section>
        <Section title={`Today's passes (${passes.length})`} testId="gate-today-log" empty={!passes.length && "No passes today."}>
          {passes.map((p) => <PassRow key={p.id} p={p} />)}
        </Section>
        <Section title={`Other vehicles today (${log.length})`} testId="gate-vehicle-log" empty={!log.length && "No vehicles logged today."}>
          {log.map((e) => <LogRow key={e.id} e={e} />)}
        </Section>
      </main>
    </div>
  );
}
